/**
 * The Activity tab, compact: what happened on the estimate as one list, one
 * line per entry, the date, time and actor muted, filtered by chips — All ·
 * Customer · Staff.
 *
 * Built from the estimate's notes. Tharstern's notes carry no date, so they
 * come last, each saying so in an ⓘ.
 */
import { useState } from "react";
import type { EstimateNote } from "@shared-core/data/api-types";
import { noteKindLabel } from "@shared-core/domain/estimate-presentation";
import {
  formatShortDateOrDash,
  formatTimeOrDash,
} from "@shared-core/domain/formatters";
import { Badge } from "../../app/components/primitives/badge";
import { cn } from "../../app/components/primitives/utils";
import { InfoTip } from "../../app/components/patterns/InfoTip";
import { Gap, Section, TabContainer } from "./atoms";

type Actor = "Customer" | "Staff";

interface ActivityEntry {
  key: string;
  /** ISO datetime, or null when the source carries no date. */
  at: string | null;
  actor: Actor;
  /** Who, when the source names them. */
  by: string | null;
  what: string;
  body: string | null;
  visibleToCustomer: boolean;
}

/** "Nothing from <who> on this estimate", per chip. */
const ACTOR_PHRASE: Record<Actor, string> = {
  Customer: "the customer",
  Staff: "staff",
};

const CHIPS: Array<"All" | Actor> = ["All", "Customer", "Staff"];

function entriesOf(notes: EstimateNote[]): ActivityEntry[] {
  return notes.map((note, i) => ({
    key: `note-${i}`,
    at: null,
    // Every note is written by staff — a note *to* the customer included;
    // whether the customer sees it is its own badge, not its author.
    actor: "Staff",
    by: null,
    what: noteKindLabel(note.kind),
    body: note.body,
    visibleToCustomer: note.visible_to_customer,
  }));
}

export function ActivityTab({
  notes,
}: {
  /** The estimate's notes, already without a repeat of its description. */
  notes: EstimateNote[];
}) {
  const [filter, setFilter] = useState<"All" | Actor>("All");
  const entries = entriesOf(notes);
  const chips = CHIPS;
  const shown = entries.filter((e) => filter === "All" || e.actor === filter);
  // One list, newest first; notes carry no date, so they come last.
  const ordered = [
    ...shown.filter((e) => e.at).sort((x, y) => (x.at! < y.at! ? 1 : -1)),
    ...shown.filter((e) => !e.at),
  ];
  return (
    <TabContainer label="Activity">
      <Section label="Notes">
        <div
          role="group"
          aria-label="Filter activity"
          className="flex flex-wrap gap-1.5"
        >
          {chips.map((chip) => (
            <button
              key={chip}
              type="button"
              aria-pressed={filter === chip}
              onClick={() => setFilter(chip)}
              className={cn(
                "h-7 rounded-full border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                filter === chip
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {chip}
            </button>
          ))}
        </div>
        {entries.length === 0 && (
          <p className="text-sm">
            <Gap>No notes on this estimate</Gap>
          </p>
        )}
        {entries.length > 0 && shown.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nothing from {filter === "All" ? "anyone" : ACTOR_PHRASE[filter]} on
            this estimate.
          </p>
        )}
        {ordered.length > 0 && (
          <ul className="divide-y divide-border">
            {ordered.map((entry) => (
              <Entry key={entry.key} entry={entry} />
            ))}
          </ul>
        )}
      </Section>
    </TabContainer>
  );
}

/** One line per event: when · what · who, the when and who muted. */
function Entry({ entry }: { entry: ActivityEntry }) {
  return (
    <li className="py-2 text-sm first:pt-0">
      <span className="text-muted-foreground tabular-nums">
        {entry.at ? (
          `${formatShortDateOrDash(entry.at)} ${formatTimeOrDash(entry.at)}`
        ) : (
          <span className="inline-flex items-center gap-1">
            No date
            <InfoTip label="Why this note has no date">
              Notes come from Tharstern, which records no date or author on
              them.
            </InfoTip>
          </span>
        )}{" "}
        ·{" "}
      </span>
      <span className="font-medium">{entry.what}</span>
      <span className="text-muted-foreground">
        {" "}
        · {[entry.actor, entry.by].filter(Boolean).join(", ")}
      </span>
      {entry.visibleToCustomer && (
        <Badge variant="neutral" className="ml-2">
          Visible to the customer
        </Badge>
      )}
      {entry.body && (
        <span className="mt-0.5 block whitespace-pre-line text-foreground">
          {entry.body}
        </span>
      )}
    </li>
  );
}
